import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { generateEncryptionKey, encryptFile } from '../utils/crypto';
import { 
  FaUpload, FaLock, FaFileAlt, FaCheckCircle, 
  FaEnvelope, FaUser, FaComment, FaSpinner
} from 'react-icons/fa';
import JSZip from 'jszip';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

function PortalSubmission() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [success, setSuccess] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [message, setMessage] = useState('');
  const fileInputRef = useRef();

  useEffect(() => {
    fetchPortalInfo();
  }, [portalId]);

  const fetchPortalInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/receive/${portalId}`);
      setPortal(response.data.portal);
    } catch (err) {
      setError(err.response?.data?.message || 'Portal not found');
    } finally {
      setLoading(false);
    }
  };

  // generateEncryptionKey and encryptFile imported from utils/crypto.js
  // Uses AES-256-GCM for authenticated encryption

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    if (portal?.settings?.requireEmail && !submitterEmail) {
      setError('Email is required for this portal');
      return;
    }

    setError('');
    setUploading(true);
    setUploadStatus('Preparing files...');

    try {
      const key = generateEncryptionKey();
      // IV is now embedded in the encrypted data for AES-GCM
      const ivArray = new Uint8Array(16);
      crypto.getRandomValues(ivArray);
      const iv = Array.from(ivArray, b => b.toString(16).padStart(2, '0')).join('');
      const fileId = uuidv4();
      const expiration = 24 * 60 * 60; // 24 hours
      const maxDownloads = 1;

      let uploadBlob, originalName;

      if (selectedFiles.length === 1) {
        const singleFile = selectedFiles[0];
        originalName = singleFile.name;
        setUploadStatus('Encrypting file...');
        const encryptedContent = await encryptFile(singleFile, key);
        uploadBlob = new Blob([encryptedContent], { type: 'text/plain' });
      } else {
        setUploadStatus('Zipping files...');
        const zip = new JSZip();
        for (const f of selectedFiles) {
          zip.file(f.name, f);
        }
        const zippedContent = await zip.generateAsync({ type: 'blob' });
        originalName = 'miragedocs-archive.zip';
        setUploadStatus('Encrypting zip...');
        const encryptedContent = await encryptFile(zippedContent, key);
        uploadBlob = new Blob([encryptedContent], { type: 'text/plain' });
      }

      setUploadStatus('Uploading...');
      const formData = new FormData();
      formData.append('chunk', uploadBlob, originalName);
      formData.append('fileId', fileId);
      formData.append('chunkIndex', '0');
      formData.append('totalChunks', '1');
      formData.append('originalName', originalName);
      formData.append('key', key);
      formData.append('iv', iv);
      formData.append('publicKey', key);
      formData.append('expiration', expiration);
      formData.append('maxDownloads', maxDownloads);
      if (submitterEmail) formData.append('submitterEmail', submitterEmail);
      if (submitterName) formData.append('submitterName', submitterName);
      if (message) formData.append('message', message);

      await axios.post(`${API_URL}/receive/${portalId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess(true);
      setUploadStatus('');
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
      setUploadStatus('');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-[#2186EB] mx-auto mb-4" />
          <p className="text-gray-600">Loading portal...</p>
        </div>
      </div>
    );
  }

  if (error && !portal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Portal Not Found</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <a href="/" className="text-[#2186EB] hover:underline">Return to home</a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">File Submitted Successfully!</h2>
          <p className="text-gray-600 mb-6">
            Your file has been securely uploaded and encrypted. The portal owner will be notified.
          </p>
          <button
            onClick={() => {
              setSuccess(false);
              setSelectedFiles([]);
              setSubmitterEmail('');
              setSubmitterName('');
              setMessage('');
            }}
            className="px-6 py-2 bg-[#2186EB] text-white rounded-lg hover:bg-blue-700 transition"
          >
            Submit Another File
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Portal Info */}
        <div 
          className="bg-white rounded-2xl shadow-lg p-8 mb-6"
          style={{
            borderTop: portal?.branding?.primaryColor ? `4px solid ${portal.branding.primaryColor}` : undefined
          }}
        >
          {portal?.branding?.logo && (
            <div className="text-center mb-4">
              <img
                src={portal.branding.logo}
                alt="Portal logo"
                className="max-h-20 mx-auto"
              />
            </div>
          )}
          <h1 
            className="text-3xl font-bold mb-2 text-center"
            style={{ color: portal?.branding?.primaryColor || '#1f2937' }}
          >
            {portal?.name}
          </h1>
          {portal?.branding?.customMessage && (
            <p className="text-gray-700 mb-4 text-center">{portal.branding.customMessage}</p>
          )}
          {portal?.description && (
            <p className="text-gray-600 mb-4 text-center">{portal.description}</p>
          )}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <FaLock className="text-[#2186EB]" />
            <span>AES-256 encrypted in transit & storage</span>
          </div>
        </div>

        {/* Upload Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Submit File</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              {error}
            </div>
          )}

          {uploadStatus && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
              {uploadStatus}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaFileAlt className="inline mr-2" />
                Select File(s)
              </label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#2186EB] transition"
              >
                <FaUpload className="text-4xl text-gray-400 mx-auto mb-4" />
                {selectedFiles.length > 0 ? (
                  <div>
                    <p className="font-medium text-gray-900">
                      {selectedFiles.length} file(s) selected
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedFiles.map(f => f.name).join(', ')}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-gray-600">Click or drag files here</p>
                    <p className="text-sm text-gray-500 mt-1">
                      Max size: {portal?.settings?.maxFileSizeMB || 100}MB
                    </p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Optional Fields */}
            {portal?.settings?.requireEmail && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={submitterEmail}
                  onChange={(e) => setSubmitterEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB]"
                  placeholder="your@email.com"
                />
              </div>
            )}

            {!portal?.settings?.requireEmail && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email Address (optional)
                </label>
                <input
                  type="email"
                  value={submitterEmail}
                  onChange={(e) => setSubmitterEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB]"
                  placeholder="your@email.com"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaUser className="inline mr-2" />
                Your Name (optional)
              </label>
              <input
                type="text"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB]"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaComment className="inline mr-2" />
                Message (optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2186EB]"
                placeholder="Add a message for the portal owner..."
              />
            </div>

            <button
              type="submit"
              disabled={uploading || selectedFiles.length === 0}
              className="w-full py-4 bg-[#FF7417] text-white rounded-lg font-semibold hover:bg-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <FaSpinner className="animate-spin" /> {uploadStatus || 'Uploading...'}
                </>
              ) : (
                <>
                  <FaUpload /> Submit File
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        {portal?.branding?.showPoweredBy !== false && (
          <div className="text-center mt-8 text-sm text-gray-500">
            Powered by <span className="font-semibold">MirageDocs</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PortalSubmission;

